import copy from '@product/copy.json'
import flows from '@product/flows.json'
import screens from '@product/screens.json'
import tokens from '@product/design-tokens.json'

const strings = copy.en as Record<string, string>

export function t(key: string, variables: Record<string, string | number> = {}): string {
  const template = strings[key]
  if (template === undefined) throw new Error(`copy.json is missing key "${key}"`)
  return template.replace(/\{(\w+)\}/g, (match, name: string) => name in variables ? String(variables[name]) : match)
}

export const productFlows = flows
export const productScreens = screens
export const designTokens = tokens

export const implementedStateNames = [
  'loading', 'empty', 'emptyFiltered', 'error', 'offline', 'notFound', 'ready', 'loadingMore',
  'submitting', 'confirming', 'pending', 'full', 'banned', 'closed', 'submitted', 'expired',
] as const

export type ScreenId = (typeof screens.screens)[number]['id']

export function applyTheme(appearance: 'system' | 'light' | 'dark' = 'system'): void {
  document.documentElement.dataset.appearance = appearance
  const root = document.documentElement.style
  const dark = tokens.color as Record<string, string>
  const light = tokens.colorLight as Record<string, string>
  for (const [key, value] of Object.entries(dark)) root.setProperty(`--color-${key}`, value)
  for (const [key, value] of Object.entries(light)) root.setProperty(`--light-${key}`, value)
  for (const [key, value] of Object.entries(tokens.space)) root.setProperty(`--space-${key}`, `${value}px`)
  for (const [key, value] of Object.entries(tokens.radius)) root.setProperty(`--radius-${key}`, `${value}px`)
  for (const [key, value] of Object.entries(tokens.type)) root.setProperty(`--type-${key}`, `${value}px`)
  for (const [key, value] of Object.entries(tokens.weight)) root.setProperty(`--weight-${key}`, String(value))
  for (const [key, value] of Object.entries(tokens.lineHeight)) root.setProperty(`--line-${key}`, String(value))
  for (const [key, value] of Object.entries(tokens.border)) root.setProperty(`--border-${key}`, `${value}px`)
  for (const [key, value] of Object.entries(tokens.avatar).filter(([, value]) => typeof value === 'number')) root.setProperty(`--avatar-${key}`, `${value}px`)
  root.setProperty('--content-max', `${tokens.layout.contentMaxWidth}px`)
  root.setProperty('--dashboard-max', `${tokens.layout.dashboardMaxWidth}px`)
  root.setProperty('--sidebar-width', `${tokens.layout.sidebarWidth}px`)
  root.setProperty('--tap-target', `${tokens.layout.minTapTarget}px`)
  root.setProperty('--row-height', `${tokens.layout.listRowMinHeight}px`)
  root.setProperty('--screen-padding', `${tokens.layout.screenPadding}px`)
  root.setProperty('--font-sans', tokens.fontFamily.sans.Web)
  root.setProperty('--font-mono', tokens.fontFamily.mono.Web)
  root.setProperty('--motion-fast', `${tokens.motion.fast}ms`)
  root.setProperty('--motion-normal', `${tokens.motion.normal}ms`)
  root.setProperty('--motion-easing', tokens.motion.easing)
  root.setProperty('--qr-size', `${tokens.qr.size}px`)
  root.setProperty('--qr-quiet-zone', `${tokens.qr.quietZone}px`)
}

export function routeScreen(pathname: string): { id: ScreenId; params: Record<string, string> } {
  const routes: [RegExp, ScreenId, string[]][] = [
    [/^\/$/, 'landing', []], [/^\/signin$/, 'signIn', []], [/^\/signin\/verify$/, 'verifyCode', []],
    [/^\/signin\/password$/, 'signInPassword', []], [/^\/auth\/google\/callback$/, 'authCallback', []],
    [/^\/inbox$/, 'inbox', []], [/^\/n\/([^/]+)$/, 'notificationDetail', ['id']],
    [/^\/channels$/, 'channels', []], [/^\/channels\/join$/, 'channelJoin', []],
    [/^\/join\/([^/]+)$/, 'channelPreview', ['code']], [/^\/channels\/([^/]+)$/, 'channelDetail', ['id']],
    [/^\/endpoints$/, 'endpoints', []], [/^\/endpoints\/new$/, 'endpointCreate', []],
    [/^\/endpoints\/([^/]+)$/, 'endpointDetail', ['id']], [/^\/endpoints\/([^/]+)\/compose$/, 'compose', ['id']],
    [/^\/endpoints\/([^/]+)\/members$/, 'channelMembers', ['id']], [/^\/endpoints\/([^/]+)\/invite$/, 'channelInvite', ['id']],
    [/^\/endpoints\/([^/]+)\/settings$/, 'endpointSettings', ['id']], [/^\/endpoints\/([^/]+)\/log$/, 'deliveryLog', ['id']],
    [/^\/endpoints\/([^/]+)\/log\/([^/]+)$/, 'deliveryDetail', ['id', 'deliveryId']],
    [/^\/devices$/, 'devices', []], [/^\/settings$/, 'settings', []], [/^\/docs$/, 'docs', []],
  ]
  for (const [pattern, id, keys] of routes) {
    const match = pattern.exec(pathname)
    if (match) return { id, params: Object.fromEntries(keys.map((key, index) => [key, decodeURIComponent(match[index + 1])])) }
  }
  return { id: 'notFound', params: {} }
}
